import { Component, HostBinding, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FeatureService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  NavigationService,
  OutlookPreferences,
  OutlookService,
  OutlookStore,
  PreferencesService,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';
import { catchError, finalize, of, tap } from 'rxjs';

@Component({
  selector: 'maximizer-outlook-preferences',
  templateUrl: './preferences.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_TOOLTIP,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class OutlookPreferencesComponent implements OnInit {
  @HostBinding('class.max-outlook') hostClass = true;

  @ViewChild('notification')
  notification!: OutlookNotificationComponent;

  form: FormGroupWrapper<OutlookPreferences>;
  loading = false;
  originalPreferences?: OutlookPreferences;

  isOutlookSyncFeatureFlagEnabled = false;
  get userHasInsertRights(): boolean {
    return this.globalStore.session()?.user.rights.documents.insert ?? false;
  }

  get detailPreferencesDisabled(): boolean {
    return !this.userHasInsertRights;
  }

  get saveNowDisabled(): boolean {
    return this.saveNowOnByDefault || !this.userHasInsertRights;
  }
  get saveNowOnByDefault(): boolean {
    return (
      !this.outlookStore.syncConfiguration()?.enabled ||
      this.outlookStore.isSharedMailbox() ||
      this.outlookService.isOutlookLegacyDesktop() ||
      !this.isOutlookSyncFeatureFlagEnabled ||
      !this.outlookStore.isCloud()
    );
  }

  constructor(
    private readonly navigationService: NavigationService,
    private readonly preferencesService: PreferencesService,
    private readonly tracker: TrackerService,
    private readonly feature: FeatureService,
    private readonly outlookService: OutlookService,
    public outlookStore: OutlookStore,
    public globalStore: GlobalStore,
  ) {
    this.isOutlookSyncFeatureFlagEnabled = this.feature.isFeatureOn(
      'microsoft-outlook-sync-emails-shown',
      false,
    );
    this.form = FormWrapperBuilder.group<OutlookPreferences>({
      saveEmailDetail: [
        { value: false, disabled: this.detailPreferencesDisabled },
        Validators.required,
      ],
      saveNow: [
        { value: false, disabled: this.saveNowDisabled },
        Validators.required,
      ],
      saveAppointmentDetail: [false, Validators.required],
    });
  }

  ngOnInit(): void {
    let preferences = this.outlookStore.preferences();
    this.loadSaveEmailLogic(preferences);

    if (!preferences.saveNow) {
      preferences.saveNow = this.saveNowOnByDefault;
    }
    this.form.patch(preferences);
    this.originalPreferences = { ...preferences };
  }

  save(): void {
    if (this.form.valid) {
      this.loading = true;
      const preferences = this.form.value;
      let success = false;

      this.preferencesService
        .updatePreferences(preferences)
        .pipe(
          catchError(() => of(null)),
          tap((setting) => {
            success = !!setting;
            this.notification.showMessage(
              success ? 'success' : 'error',
              success
                ? 'outlook.preferences.saved'
                : 'outlook.preferences.error',
              true,
            );
          }),
          finalize(() => {
            this.loading = false;
            if (success) {
              this.form.group.markAsPristine();
            }

            this.trackCompletedEvent(success);
          }),
        )
        .subscribe();
    }
  }

  private trackCompletedEvent(success: boolean): void {
    this.tracker.trackEvent({
      module: EventModule.Preferences,
      journey: EventJourney.Update,
      action: EventAction.Completed,
      metadata: {
        success: success ? EventMetadata.Success : EventMetadata.Failed,
        saveEmailDetail: this.form.value.saveEmailDetail,
        saveNow: this.form.value.saveNow,
        saveAppointmentDetail: this.form.value.saveAppointmentDetail,
      },
    });
  }

  cancel(): void {
    this.navigationService.navigateBack();
  }

  private loadSaveEmailLogic(preferences: OutlookPreferences): void {
    if (!this.isOutlookSyncFeatureFlagEnabled && this.userHasInsertRights) {
      preferences.saveNow = true;
    } else if (!this.outlookStore.versionValidForNewSync()) {
      preferences.saveNow = true;
    } else {
      preferences.saveNow = this.userHasInsertRights
        ? preferences.saveNow
        : false;
    }
    preferences.saveEmailDetail = this.userHasInsertRights
      ? preferences.saveEmailDetail
      : false;
    preferences.saveAppointmentDetail =
      preferences.saveAppointmentDetail ?? false;
  }
}
